import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Users as UsersIcon,
  UserPlus,
  ShieldCheck,
  KeyRound,
  Copy,
  Check,
  X,
  Eye,
} from "lucide-react";
import { toast } from "sonner";

const ROLE_BADGE = {
  org_admin: "bg-ek-purple/15 text-ek-purple",
  executive: "bg-indigo-100 text-indigo-700",
  manager: "bg-blue-100 text-blue-700",
  contributor: "bg-emerald-100 text-emerald-700",
  viewer: "bg-gray-100 text-gray-600",
  admin: "bg-ek-purple/15 text-ek-purple",
  cro: "bg-indigo-100 text-indigo-700",
  ciso: "bg-cyan-100 text-cyan-700",
  cpo: "bg-amber-100 text-amber-700",
  cfo: "bg-rose-100 text-rose-700",
};

const CLEARANCE_LABEL = ["", "L1 General", "L2 Internal", "L3 Restricted", "L4 Confidential", "L5 Executive"];

const TempPasswordBanner = ({ value, onClose }) => {
  const [copied, setCopied] = useState(false);
  return (
    <div
      className="mt-6 flex items-center justify-between gap-4 rounded-md border border-emerald-300 bg-emerald-50 px-5 py-4"
      data-testid="temp-password-banner"
    >
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
          Temporary password — shown once
        </p>
        <code className="mt-1 block font-mono text-lg font-bold text-ek-ink" data-testid="temp-password-value">
          {value}
        </code>
        <p className="mt-1 text-xs text-emerald-700/80">
          Share securely. The user must change it on first sign-in.
        </p>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => {
            navigator.clipboard?.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
          data-testid="copy-temp-password"
          className="flex items-center gap-1.5 rounded-sm border border-emerald-400 px-3 py-1.5 text-sm font-semibold text-emerald-700 hover:bg-emerald-100"
        >
          {copied ? <Check size={14} /> : <Copy size={14} />}
          {copied ? "Copied" : "Copy"}
        </button>
        <button onClick={onClose} data-testid="dismiss-temp-password" className="text-emerald-700/60 hover:text-emerald-900">
          <X size={18} />
        </button>
      </div>
    </div>
  );
};

const UserModal = ({ catalogue, editing, onClose, onSaved }) => {
  const isEdit = Boolean(editing);
  const [name, setName] = useState(editing?.name || "");
  const [email, setEmail] = useState(editing?.email || "");
  const [role, setRole] = useState(editing?.role || "viewer");
  const [clearance, setClearance] = useState(editing?.clearance || 2);
  const [domains, setDomains] = useState(
    editing?.domains?.includes("*") ? [] : editing?.domains || []
  );
  const [businessUnits, setBusinessUnits] = useState((editing?.business_units || []).join(", "));
  const [sendInvite, setSendInvite] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const roleMeta = catalogue.roles.find((r) => r.role === role);
  // editing a legacy persona role not in the assignable list — allow keeping it
  const allAccess = roleMeta?.all_access;

  const toggleDomain = (id) =>
    setDomains((d) => (d.includes(id) ? d.filter((x) => x !== id) : [...d, id]));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const payload = {
      name,
      role,
      clearance: Number(clearance),
      domains: allAccess ? ["*"] : domains,
      business_units: businessUnits.split(",").map((b) => b.trim().toLowerCase()).filter(Boolean),
    };
    try {
      if (isEdit) {
        const { data } = await api.patch(`/admin/users/${editing.user_id}`, payload);
        onSaved({ kind: "updated", user: data.user });
      } else {
        const { data } = await api.post("/admin/users", { ...payload, email, send_invite: sendInvite });
        onSaved({ kind: "created", user: data.user, temp: data.temporary_password, invite: data.invite });
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 p-4" data-testid="user-modal">
      <div className="w-full max-w-lg rounded-md border border-black/10 bg-white p-7 shadow-2xl">
        <div className="flex items-center justify-between">
          <h2 className="ek-heading text-2xl">{isEdit ? "Edit user" : "Invite user"}</h2>
          <button onClick={onClose} data-testid="user-modal-close" className="text-ek-ink/65 hover:text-ek-ink">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={submit} className="mt-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-ek-ink/60">Full name</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                data-testid="user-name-input"
                className="mt-1 w-full rounded-sm border border-black/15 px-3 py-2 text-sm focus:border-ek-purple focus:outline-none"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-ek-ink/60">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isEdit}
                data-testid="user-email-input"
                className="mt-1 w-full rounded-sm border border-black/15 px-3 py-2 text-sm focus:border-ek-purple focus:outline-none disabled:bg-ek-canvas disabled:text-ek-ink/65"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-ek-ink/60">Role (capability tier)</label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                data-testid="user-role-select"
                className="mt-1 w-full rounded-sm border border-black/15 px-3 py-2 text-sm focus:border-ek-purple focus:outline-none"
              >
                {catalogue.roles.map((r) => (
                  <option key={r.role} value={r.role}>
                    {r.label}
                  </option>
                ))}
                {isEdit && !catalogue.roles.find((r) => r.role === role) && (
                  <option value={role}>{role}</option>
                )}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-ek-ink/60">Clearance (sensitivity)</label>
              <select
                value={clearance}
                onChange={(e) => setClearance(e.target.value)}
                data-testid="user-clearance-select"
                className="mt-1 w-full rounded-sm border border-black/15 px-3 py-2 text-sm focus:border-ek-purple focus:outline-none"
              >
                {catalogue.clearances.map((c) => (
                  <option key={c.level} value={c.level}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-ek-ink/60">
              Domains (risk areas this user may see)
            </label>
            {allAccess ? (
              <p className="mt-2 rounded-sm bg-ek-purple/5 px-3 py-2 text-sm text-ek-purple" data-testid="all-domains-note">
                This role has enterprise-wide visibility (all domains).
              </p>
            ) : (
              <div className="mt-2 grid grid-cols-2 gap-1.5" data-testid="domain-grid">
                {catalogue.domains.map((d) => (
                  <label
                    key={d.theme_id}
                    data-testid={`domain-option-${d.theme_id}`}
                    className={`flex cursor-pointer items-center gap-2 rounded-sm border px-2.5 py-1.5 text-xs transition-colors ${
                      domains.includes(d.theme_id)
                        ? "border-ek-purple bg-ek-purple/5 text-ek-ink"
                        : "border-black/10 text-ek-ink/60 hover:border-black/25"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={domains.includes(d.theme_id)}
                      onChange={() => toggleDomain(d.theme_id)}
                      className="accent-ek-purple"
                    />
                    {d.label}
                  </label>
                ))}
              </div>
            )}
          </div>

          {error && (
            <p className="text-sm text-red-600" data-testid="user-modal-error">
              {error}
            </p>
          )}
          <div>
            <label className="mb-1 block text-sm font-medium text-ek-ink">Business units <span className="font-normal text-ek-ink/65">(optional — restricts this user to these units)</span></label>
            <input
              value={businessUnits}
              onChange={(e) => setBusinessUnits(e.target.value)}
              data-testid="user-business-units-input"
              placeholder="e.g. north, emea"
              className="w-full rounded-lg border border-black/10 bg-white px-3 py-2 text-sm outline-none focus:border-ek-purple focus:ring-2 focus:ring-ek-purple/20"
            />
            <p className="mt-1 text-xs text-ek-ink/65">Comma-separated. Leave blank for organisation-wide access. Enforced when business-unit scoping is on in Access policy.</p>
          </div>
          {!isEdit && (
            <label className="flex items-center gap-2 text-sm text-ek-ink/75">
              <input
                type="checkbox"
                checked={sendInvite}
                onChange={(e) => setSendInvite(e.target.checked)}
                data-testid="send-invite-checkbox"
                className="accent-ek-purple"
              />
              Email an invite with the temporary password to this user
            </label>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="rounded-sm px-4 py-2 text-sm font-semibold text-ek-ink/60 hover:text-ek-ink">
              Cancel
            </button>
            <button type="submit" disabled={busy} data-testid="user-modal-submit" className="ek-btn disabled:opacity-50">
              {busy ? "Saving…" : isEdit ? "Save changes" : "Create user"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default function Users() {
  const { user: me, viewAs } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [catalogue, setCatalogue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // {editing} | "new"
  const [temp, setTemp] = useState(null);

  const reload = () =>
    api.get("/admin/users").then(({ data }) => setRows(data.items));

  useEffect(() => {
    Promise.all([api.get("/admin/users"), api.get("/admin/rbac/catalogue")])
      .then(([u, c]) => {
        setRows(u.data.items);
        setCatalogue(c.data);
      })
      .catch(() => toast.error("Failed to load users"))
      .finally(() => setLoading(false));
  }, []);

  const domainLabel = useMemo(() => {
    const map = {};
    (catalogue?.domains || []).forEach((d) => (map[d.theme_id] = d.label));
    return map;
  }, [catalogue]);

  const onSaved = (res) => {
    setModal(null);
    reload();
    if (res.kind === "created") {
      setTemp(res.temp);
      if (res.invite?.sent) toast.success(`User ${res.user.email} created — invite emailed`);
      else toast.success(`User ${res.user.email} created`);
    } else {
      toast.success("User updated");
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/admin/users/${u.user_id}`, { active: !u.active });
      toast.success(u.active ? "User deactivated" : "User activated");
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const resetPassword = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.user_id}/reset-password`);
      setTemp(data.temporary_password);
      toast.success("Temporary password generated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const previewAs = async (u) => {
    try {
      await viewAs(u.user_id);
      toast.success(`Previewing as ${u.name}`);
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Cannot preview as this user");
    }
  };

  return (
    <div className="min-h-screen bg-ek-canvas">
      <Navbar />
      <main className="mx-auto max-w-7xl px-6 py-12" data-testid="users-page">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="ek-eyebrow">Administration · Security</p>
            <h1 className="ek-heading mt-2 text-4xl sm:text-5xl">Users &amp; access</h1>
            <p className="mt-3 max-w-2xl text-base text-ek-ink/65">
              Provision users with a capability <strong>role</strong>, the <strong>domains</strong> (risk
              areas) they may see, and a sensitivity <strong>clearance</strong>. Sensitive items are hidden
              from staff below the required clearance — the CRO and administrators see everything.
            </p>
          </div>
          <button
            onClick={() => setModal("new")}
            data-testid="add-user-button"
            className="ek-btn flex items-center gap-2"
          >
            <UserPlus size={16} /> Invite user
          </button>
        </div>

        {temp && <TempPasswordBanner value={temp} onClose={() => setTemp(null)} />}

        <div className="mt-8 overflow-hidden rounded-md border border-black/5 bg-white">
          {loading ? (
            <div className="py-20 text-center text-sm text-ek-ink/65">Loading users…</div>
          ) : (
            <table className="w-full text-left text-sm" data-testid="users-table">
              <thead className="border-b border-black/5 bg-ek-canvas/50">
                <tr className="text-xs uppercase tracking-wide text-ek-ink/65">
                  <th className="px-5 py-3 font-semibold">User</th>
                  <th className="px-5 py-3 font-semibold">Role</th>
                  <th className="px-5 py-3 font-semibold">Domains</th>
                  <th className="px-5 py-3 font-semibold">Clearance</th>
                  <th className="px-5 py-3 font-semibold">Status</th>
                  <th className="px-5 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => (
                  <tr
                    key={u.user_id}
                    data-testid={`user-row-${u.email}`}
                    className="border-b border-black/5 last:border-0 transition-colors hover:bg-ek-purple/[0.03]"
                  >
                    <td className="px-5 py-3">
                      <div className="font-semibold text-ek-ink">{u.name}</div>
                      <div className="text-xs text-ek-ink/65">{u.email}</div>
                    </td>
                    <td className="px-5 py-3">
                      <span className={`rounded-md px-2 py-0.5 text-xs font-semibold ${ROLE_BADGE[u.role] || "bg-gray-100 text-gray-600"}`}>
                        {u.role}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      {u.domains?.includes("*") ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-ek-purple">
                          <ShieldCheck size={13} /> All domains
                        </span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {(u.domains || []).map((d) => (
                            <span key={d} className="rounded-sm bg-ek-ink/5 px-1.5 py-0.5 text-[0.68rem] text-ek-ink/60">
                              {domainLabel[d] || d}
                            </span>
                          ))}
                          {(!u.domains || u.domains.length === 0) && (
                            <span className="text-xs text-ek-ink/30">none</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3 text-xs text-ek-ink/70">{CLEARANCE_LABEL[u.clearance] || u.clearance}</td>
                    <td className="px-5 py-3">
                      <span
                        data-testid={`user-status-${u.email}`}
                        className={`rounded-md px-2 py-0.5 text-xs font-semibold ${
                          u.active ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"
                        }`}
                      >
                        {u.active ? "Active" : "Inactive"}
                      </span>
                      {u.must_change_password && (
                        <span className="ml-1.5 text-[0.68rem] text-amber-600">pending</span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        {u.user_id !== me.user_id && u.role !== "admin" && u.role !== "org_admin" && (
                          <button
                            onClick={() => previewAs(u)}
                            data-testid={`view-as-${u.email}`}
                            title="View the app as this user"
                            className="flex items-center gap-1 rounded-sm border border-amber-300 px-2.5 py-1 text-xs font-semibold text-amber-700 hover:bg-amber-50"
                          >
                            <Eye size={12} /> View as
                          </button>
                        )}
                        <button
                          onClick={() => setModal({ editing: u })}
                          data-testid={`edit-user-${u.email}`}
                          className="rounded-sm border border-black/10 px-2.5 py-1 text-xs font-semibold text-ek-ink/70 hover:border-ek-purple hover:text-ek-purple"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => resetPassword(u)}
                          data-testid={`reset-password-${u.email}`}
                          title="Reset password"
                          className="flex items-center gap-1 rounded-sm border border-black/10 px-2.5 py-1 text-xs font-semibold text-ek-ink/70 hover:border-ek-purple hover:text-ek-purple"
                        >
                          <KeyRound size={12} />
                        </button>
                        {u.user_id !== me.user_id && u.role !== "admin" && u.role !== "org_admin" && (
                          <button
                            onClick={() => toggleActive(u)}
                            data-testid={`toggle-active-${u.email}`}
                            className={`rounded-sm border px-2.5 py-1 text-xs font-semibold ${
                              u.active
                                ? "border-red-200 text-red-600 hover:bg-red-50"
                                : "border-emerald-200 text-emerald-600 hover:bg-emerald-50"
                            }`}
                          >
                            {u.active ? "Deactivate" : "Activate"}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="mt-6 flex items-start gap-2 text-xs text-ek-ink/65">
          <UsersIcon size={14} className="mt-0.5 shrink-0" />
          <p>
            The Organisation Profile (sector, size, footprint) is set once by an administrator and is
            read-only for everyone else. Operational users only see the risk domains assigned to them.
          </p>
        </div>
      </main>

      {modal && catalogue && (
        <UserModal
          catalogue={catalogue}
          editing={modal === "new" ? null : modal.editing}
          onClose={() => setModal(null)}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}
